import React, { useCallback, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { LoadingOutlined } from '@ant-design/icons';
import { Divider, Popover, Space, Spin } from 'antd';

import { ASSET_TYPES } from '@/shared/api/openmetadata';
import { OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { DomainEditPopover } from '@/shared/ui/DomainEditPopover';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { MetadataItem } from '@/shared/ui/MetadataItem';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { OwnerEditPopover } from '@/shared/ui/OwnerEditPopover';
import { TierEditPopover } from '@/shared/ui/TierEditPopover';

import { useDisplaySettingsStore } from '@/entities/display-settings';

import type { AssetBaseInfo } from '@/features/asset-detail/api';
import {
  useAssetDomainEdit,
  useAssetOwnerEdit,
  useAssetSummaryDisplay,
  useAssetTierEdit,
} from '@/features/asset-detail/model';

import styles from './AssetDetailSummaryCard.module.css';

type AssetDetailSummaryCardProps = {
  assetData: AssetBaseInfo | null;
  entityType: string;
  serviceType?: string;
  fqn: string;
  overrideCreatedAt?: number;
  overrideUpdatedAt?: number;
};

export const AssetDetailSummaryCard: React.FC<AssetDetailSummaryCardProps> = ({
  assetData,
  entityType,
  serviceType,
  fqn,
  overrideCreatedAt,
  overrideUpdatedAt,
}) => {
  const navigate = useNavigate();
  const summaryDisplay = useAssetSummaryDisplay({ assetData, entityType });
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const domainEdit = useAssetDomainEdit({
    assetId: assetData?.id || '',
    entityType,
    serviceType,
    fqn,
  });

  const ownerEdit = useAssetOwnerEdit({
    assetId: assetData?.id || '',
    entityType,
    fqn,
  });

  const tierEdit = useAssetTierEdit({
    assetId: assetData?.id || '',
    entityType,
    fqn,
  });

  const isDatabaseEntity = useMemo(() => {
    return (
      entityType === ASSET_TYPES.TABLE ||
      entityType === ASSET_TYPES.DATABASE ||
      entityType === ASSET_TYPES.DATABASE_SCHEMA
    );
  }, [entityType]);

  const primaryDomain = useMemo(() => {
    const domain = assetData?.domains?.[0];
    if (!domain) return null;

    return {
      id: domain.id,
      label: domain.displayName || domain.name,
      fullyQualifiedName: domain.fullyQualifiedName,
    };
  }, [assetData?.domains]);

  const handleDomainClick = useCallback(
    (e: React.MouseEvent) => {
      if (!primaryDomain) return;
      e.stopPropagation();
      navigate(`/domain/${encodeURIComponent(primaryDomain.fullyQualifiedName)}/sub-domains`);
    },
    [primaryDomain, navigate]
  );

  const displayCreatedAt = useMemo(() => {
    if (overrideCreatedAt) {
      return formatDateTime(overrideCreatedAt);
    }
    return null;
  }, [overrideCreatedAt]);

  const displayUpdatedAt = useMemo(() => {
    if (overrideUpdatedAt) {
      return formatDateTime(overrideUpdatedAt);
    }
    return summaryDisplay.updatedAt;
  }, [overrideUpdatedAt, summaryDisplay.updatedAt]);

  return (
    <div className={styles.summaryCard}>
      <div className={styles.metadata}>
        <MetadataItem icon={<Icon name="globe" size={14} />} title="도메인">
          <div className={styles.metadataItemSpace}>
            <div className={styles.metadataContent}>
              {domainEdit.isUpdating ? (
                <Spin indicator={<LoadingOutlined spin />} size="small" />
              ) : primaryDomain ? (
                <EllipsisText text={primaryDomain.label} className={styles.itemName}>
                  <span onClick={handleDomainClick} className={styles.domainLink}>
                    {primaryDomain.label}
                  </span>
                </EllipsisText>
              ) : (
                '–'
              )}
            </div>
            <Popover
              content={<DomainEditPopover domainEdit={domainEdit} />}
              trigger="click"
              open={domainEdit.isOpen}
              arrow={false}
              onOpenChange={(visible) => (visible ? domainEdit.handleOpen() : domainEdit.handleClose())}
              placement="bottomLeft">
              <IconButton size="small" tooltip="도메인 편집">
                <Icon name="edit" size={16} />
              </IconButton>
            </Popover>
          </div>
        </MetadataItem>
        <Divider type="vertical" className={styles.metadataDivider} />
        {showOwners && (
          <>
            <MetadataItem title="소유자">
              <Space size={4} align="center" className={styles.metadataItemSpace}>
                <div className={styles.metadataContent}>
                  {assetData?.owners && assetData.owners.length > 0 ? (
                    <OwnerAvatars owners={assetData.owners} size={OWNER_AVATAR_SIZES.DETAIL} showName={true} />
                  ) : (
                    '–'
                  )}
                </div>
                <Popover
                  content={<OwnerEditPopover ownerEdit={ownerEdit} />}
                  title="소유자 편집"
                  trigger="click"
                  open={ownerEdit.isOpen}
                  onOpenChange={(visible) => (visible ? ownerEdit.handleOpen() : ownerEdit.handleClose())}
                  placement="bottomLeft">
                  <IconButton size="small" tooltip="소유자 편집">
                    <Icon name="edit" size={16} />
                  </IconButton>
                </Popover>
              </Space>
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        <MetadataItem title="중요 등급" icon={<Icon name="tier" size={14} />}>
          <div className={styles.metadataItemSpace}>
            <span className={styles.itemName}>
              {tierEdit.isUpdating ? (
                <Spin indicator={<LoadingOutlined spin />} size="small" />
              ) : (
                (summaryDisplay.tierDisplay?.tierName ?? '-')
              )}
            </span>
            <Popover
              content={<TierEditPopover tierEdit={tierEdit} currentTierFQN={summaryDisplay.tierDisplay?.tierFQN} />}
              trigger="click"
              open={tierEdit.isOpen}
              onOpenChange={(visible) => (visible ? tierEdit.handleOpen() : tierEdit.handleClose())}
              placement="bottomLeft"
              arrow={false}>
              <IconButton size="small" tooltip="중요 등급 편집">
                <Icon name="edit" size={16} />
              </IconButton>
            </Popover>
          </div>
        </MetadataItem>
        <Divider type="vertical" className={styles.metadataDivider} />
        {isDatabaseEntity && (
          <>
            <MetadataItem
              title="성숙도"
              icon={<Icon name="gem" color={summaryDisplay.certificationDisplay?.color} size={14} />}>
              <span className={styles.itemName}>{summaryDisplay.certificationDisplay?.name ?? '-'}</span>
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        {displayCreatedAt && (
          <>
            <MetadataItem title="생성일">
              <span className={styles.itemName}>{displayCreatedAt}</span>
            </MetadataItem>
            <Divider type="vertical" className={styles.metadataDivider} />
          </>
        )}
        <MetadataItem title="수정일">
          <span className={styles.itemName}>{displayUpdatedAt}</span>
        </MetadataItem>
        {summaryDisplay.rowCount && (
          <>
            <Divider type="vertical" className={styles.metadataDivider} />
            <MetadataItem title="Row 수">{summaryDisplay.rowCount}</MetadataItem>
          </>
        )}
        {summaryDisplay.tableCount && (
          <>
            <Divider type="vertical" className={styles.metadataDivider} />
            <MetadataItem title={entityType === ASSET_TYPES.DATABASE ? 'Schema 수' : 'Object 수'}>
              {summaryDisplay.tableCount}
            </MetadataItem>
          </>
        )}
      </div>
    </div>
  );
};
